import React from 'react';
import { HiOutlineOfficeBuilding, HiOutlineLogout } from 'react-icons/hi';
import { useClinic } from '../../context/ClinicContext';
import { useAuth } from '../../context/AuthContext';

/**
 * Renders a modal-style clinic picker overlay when the authenticated user has
 * memberships but no `currentClinicId` selected, OR a "no clinics" empty state
 * when they have zero memberships.
 *
 * Children are rendered normally once a clinic is selected so the rest of the
 * app can use `req.clinicId` reliably.
 */
const ClinicPickerGate = ({ children }) => {
  const { user, logout } = useAuth();
  const { clinics, currentClinicId, setCurrentClinicId, loading } = useClinic();

  // Wait for clinics to load before deciding.
  if (!user) return children;
  if (loading && clinics.length === 0) {
    return (
      <div className="fixed inset-0 bg-gray-50 flex items-center justify-center z-40">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  // User has zero clinics — block app, show empty state with logout.
  if (clinics.length === 0) {
    return (
      <div className="fixed inset-0 bg-gray-50 flex items-center justify-center z-40 px-4">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 max-w-md w-full p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <HiOutlineOfficeBuilding className="w-7 h-7 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-1">No clinic access</h2>
          <p className="text-sm text-gray-600 mb-5">
            You aren't a member of any clinic yet. Please contact your administrator
            to be added to a clinic.
          </p>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-800"
          >
            <HiOutlineLogout className="w-4 h-4" />
            Logout
          </button>
        </div>
      </div>
    );
  }

  // User has clinics but none selected — show forced picker.
  if (!currentClinicId) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 px-4">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 max-w-md w-full">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <HiOutlineOfficeBuilding className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Select a clinic</h2>
                <p className="text-xs text-gray-500">Choose which clinic to work in.</p>
              </div>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto py-2">
            {clinics.map((clinic) => (
              <button
                key={clinic.id}
                type="button"
                onClick={() => setCurrentClinicId(clinic.id)}
                className="w-full text-left px-6 py-3 hover:bg-gray-50 border-b border-gray-50 last:border-b-0"
              >
                <p className="text-sm font-medium text-gray-900 truncate">{clinic.name}</p>
                {clinic.role_name && (
                  <p className="text-xs text-gray-500 capitalize">{clinic.role_name}</p>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default ClinicPickerGate;
